
interface CadenceState {
  kb: number;
  mouse: number;
  kbDeltas: number[];
  lastKbAt: number;
  startAt: number;
}

const state: CadenceState = {
  kb: 0,
  mouse: 0,
  kbDeltas: [],
  lastKbAt: 0,
  startAt: 0,
};

let attached = false;

function onKeydown(): void {
  const now = performance.now();
  if (state.lastKbAt > 0) {
    const d = now - state.lastKbAt;

    if (d > 0 && d < 2000) state.kbDeltas.push(d);
  }
  state.lastKbAt = now;
  state.kb++;
}

function onMove(): void {
  state.mouse++;
}

export function initCadenceTracker(): void {
  if (attached) return;
  attached = true;
  state.startAt = performance.now();
  document.addEventListener("keydown", onKeydown, { passive: true });
  document.addEventListener("pointermove", onMove, { passive: true });
  document.addEventListener("touchstart", onMove, { passive: true });
}

let cachedFp: string | null = null;

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(digest);
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex;
}

function canvasHash(): string {
  try {
    const c = document.createElement("canvas");
    c.width = 220;
    c.height = 30;
    const g = c.getContext("2d");
    if (!g) return "nocanvas";
    g.textBaseline = "top";
    g.font = "14px 'Arial'";
    g.fillStyle = "#f60";
    g.fillRect(125, 1, 62, 20);
    g.fillStyle = "#069";
    g.fillText("Dropl,\u{1F6F0}\u{1F449}", 2, 15);
    g.fillStyle = "rgba(102, 204, 0, 0.7)";
    g.fillText("Dropl,\u{1F6F0}\u{1F449}", 4, 17);

    return c.toDataURL().slice(-64);
  } catch {
    return "canvas-err";
  }
}

async function computeFingerprint(): Promise<string> {
  if (cachedFp) return cachedFp;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "utc";
  const parts = [
    navigator.userAgent,
    navigator.languages?.join(",") ?? navigator.language,
    tz,
    `${screen.width}x${screen.height}`,
    `dpr=${window.devicePixelRatio}`,
    `hw=${navigator.hardwareConcurrency ?? 0}`,
    `mem=${(navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 0}`,
    `touch=${navigator.maxTouchPoints}`,
    canvasHash(),
  ];
  const hex = await sha256Hex(parts.join("|"));
  cachedFp = hex.slice(0, 32);
  return cachedFp;
}

export interface CadenceSignals {

  kb: number;

  mouse: number;

  kbVarMs: number;

  durationMs: number;
}

export interface Fingerprint {
  fp: string;
  signals: CadenceSignals;
}

function variance(xs: number[]): number {
  if (xs.length < 2) return 0;
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  const sq = xs.reduce((a, b) => a + (b - mean) ** 2, 0) / xs.length;
  return Math.round(Math.sqrt(sq));
}

export async function getFingerprint(): Promise<Fingerprint> {
  const fp = await computeFingerprint();
  return {
    fp,
    signals: {
      kb: state.kb,
      mouse: state.mouse,
      kbVarMs: variance(state.kbDeltas),
      durationMs: Math.round(performance.now() - state.startAt),
    },
  };
}
